import React, { useState, useMemo } from "react";
import { Modal, Form } from "react-bootstrap";
import { pickField } from "../utils/fieldHelpers";
import SearchableSelect from "./SearchableSelect";
import { buildCustomerConfig } from "../utils/refDataConfigs";
import { getUser } from "../utils/auth";

// ═══════════════════════════════════════════════════════════════════════════════════════
//  The contract create/edit form, shared by the Price Contracts admin page and the
//  Contract tab on a customer.
//
//  EXTRACTED RATHER THAN DUPLICATED for one reason: the signed-percent echo below. A rep
//  reading "10%" cannot tell cheaper from dearer, and typing the sign backwards overcharges
//  a hospital on every quote until somebody notices. Two copies of that sentence would
//  drift, and the copy that drifted would be the one nobody was looking at.
// ═══════════════════════════════════════════════════════════════════════════════════════

const f = (o, k, d = "") => {
  const v = pickField(o, k);
  return v === undefined || v === null ? d : v;
};


// The one field on this screen that costs money when it is typed backwards.
// Returns a tone plus a sentence with no jargon in it at all.
function adjustEcho(raw, year) {
  const yr = String(year || "").trim();
  const book = /^\d{4}$/.test(yr) ? `${yr} book` : "reference-year book";
  const s = String(raw === null || raw === undefined ? "" : raw).trim();
  if (s === "" || s === "-" || s === "+" || s === "." || s === "-.") {
    return { tone: "muted", text: `Type a signed number. Minus is cheaper than the ${book}, plus is dearer.` };
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return { tone: "bad", text: `"${s}" is not a number.` };
  if (n < -100 || n > 100) return { tone: "bad", text: "The adjustment has to be between -100 and +100." };
  if (n === 0) return { tone: "flat", text: `0 means the hospital pays the ${book} price, unchanged.` };
  if (n < 0) return { tone: "good", text: `${n} means the hospital pays ${Math.abs(n)}% LESS than the ${book}.` };
  return { tone: "warn", text: `+${n} means the hospital pays ${n}% MORE than the ${book}.` };
}

const ECHO_STYLE = {
  good:  { background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0" },
  warn:  { background: "#FFFBEB", color: "#92400E", border: "1px solid #FDE68A" },
  bad:   { background: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" },
  flat:  { background: "#EEF2FF", color: "#3730A3", border: "1px solid #C7D2FE" },
  muted: { background: "#F8FAFC", color: "#64748B", border: "1px solid #E2E8F0" },
};


const ERROR_BOX = { marginTop: 10, background: "#FEF2F2", color: "#991B1B" };

function LabeledInput({ label, hint, children }) {
  return (
    <div>
      <label className="qw-label">{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// Dates arrive as "2026-01-01" or a full ISO stamp; an <input type="date"> wants the day part
// only. Sliced rather than parsed, deliberately — new Date("2026-01-01") is UTC midnight and
// renders as 31 Dec in any negative-offset timezone, which is every US one.
const dayValue = (v) => (v ? String(v).slice(0, 10) : "");

// The reference year defaults to THIS year, not blank: a contract is nearly always pegged to
// the book the customer is currently buying from, and a wrong-but-visible default gets
// corrected where an empty field gets skipped.
const blankForm = () => ({
  contractName: "",
  contractRef: "",
  signingEntityName: "",
  signingCustNum: "",
  signingCustId: "",
  referencePriceYear: String(new Date().getFullYear()),
  contractStartDate: "",
  contractEndDate: "",
  adjustPercent: "",
  notes: "",
});

export { adjustEcho, ECHO_STYLE, LabeledInput, ERROR_BOX };

// `initial` is a contract being EDITED; `prefill` seeds a NEW one (UI-PC-005: the customer tab's
// pre-filled new contract arrived as `initial` and opened as "Edit ... price contract" with an
// empty year and the edit warning).
export default function ContractFormModal({ initial, prefill, saving, onClose, onSubmit }) {
  const editing = !!initial;
  // Built once. Scope comes from the signed-in user, so a territory-restricted rep searches
  // their own customers and an unrestricted one searches all of them — the same rule the quote
  // and customer screens already apply, rather than a second opinion about who may see whom.
  const customerCfg = useMemo(() => {
    const u = getUser() || {};
    return buildCustomerConfig(null, null, {
      isUnrestricted: !!u.isUnrestricted,
      territoryId: u.territoryId || u.territoryID || null,
      role: u.role || "", // SAL-GAP-62: Customer Service searches every customer
    });
  }, []);

  const [d, setD] = useState(() => (initial ? {
    contractName: f(initial, "contractName"),
    contractRef: f(initial, "contractRef"),
    signingEntityName: f(initial, "signingEntityName"),
    signingCustNum: f(initial, "signingCustNum") === "" ? "" : String(f(initial, "signingCustNum")),
    signingCustId: f(initial, "signingCustId"),
    referencePriceYear: String(f(initial, "referencePriceYear", "")),
    contractStartDate: dayValue(f(initial, "contractStartDate")),
    contractEndDate: dayValue(f(initial, "contractEndDate")),
    adjustPercent: String(f(initial, "adjustPercent", "")),
    notes: f(initial, "notes"),
  } : {
    ...blankForm(),
    ...(prefill ? {
      contractName: f(prefill, "contractName"),
      signingEntityName: f(prefill, "signingEntityName"),
      signingCustNum: f(prefill, "signingCustNum") === "" ? "" : String(f(prefill, "signingCustNum")),
    } : {}),
  }));
  const [err, setErr] = useState(null);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const echo = adjustEcho(d.adjustPercent, d.referencePriceYear);

  // Mirrors ContractAdminService.Validate so the obvious mistakes never leave
  // the browser. The server remains the authority — it re-checks all of this.
  const localProblem = () => {
    if (!d.contractName.trim()) return "A contract name is required.";
    if (!/^\d{4}$/.test(String(d.referencePriceYear).trim())) return "The reference price year must be a four-digit year.";
    const yr = Number(d.referencePriceYear);
    if (yr < 1990 || yr > 2200) return "That price year is not plausible.";
    if (!d.contractStartDate) return "A start date is required.";
    if (!d.contractEndDate) return "An end date is required.";
    if (d.contractEndDate < d.contractStartDate) return "The end date is before the start date.";
    const adj = Number(String(d.adjustPercent).trim());
    if (String(d.adjustPercent).trim() === "" || !Number.isFinite(adj)) return "The adjustment must be a number — use a minus sign for cheaper.";
    if (adj < -100 || adj > 100) return "The adjustment must be between -100% and +100%.";
    if (String(d.signingCustNum).trim() && !/^\d+$/.test(String(d.signingCustNum).trim())) return "The signing customer number must be a whole number.";
    return null;
  };

  const save = async () => {
    const problem = localProblem();
    if (problem) { setErr(problem); return; }
    setErr(null);
    const payload = {
      contractName: d.contractName.trim(),
      contractRef: d.contractRef.trim() || null,
      signingEntityName: d.signingEntityName.trim() || null,
      signingCustNum: String(d.signingCustNum).trim() ? Number(d.signingCustNum) : null,
      referencePriceYear: Number(d.referencePriceYear),
      contractStartDate: d.contractStartDate,
      contractEndDate: d.contractEndDate,
      adjustPercent: Number(d.adjustPercent),
      // Carried, not edited. PUT replaces the whole row — omitting this would
      // null out the link to the signed PDF on every save.
      attachmentId: editing ? (f(initial, "attachmentId", null) ?? null) : null,
      notes: d.notes.trim() || null,
    };
    if (editing) payload.rowVersion = f(initial, "rowVersion", null) ?? null;
    const res = await onSubmit(payload);
    if (res && res.ok === false && res.status === 400) setErr(res.message);
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable size="lg" contentClassName="qw-root">
      <Modal.Header closeButton={!saving}>
        <Modal.Title style={{ fontSize: 18 }}>{editing ? `Edit ${f(initial, "contractRef") || f(initial, "contractName")}` : "New price contract"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="qw-info qw-info-amber" style={{ ...ERROR_BOX, marginTop: 0, marginBottom: 12 }}><div>{err}</div></div>}

        {editing && (
          <div className="qw-info qw-info-indigo" style={{ marginBottom: 14, fontSize: 12.5 }}>
            <div>
              Changing the <strong>adjustment</strong>, the <strong>price year</strong> or either <strong>date</strong>
              {" "}takes an Active contract out of Active and clears its approval. The save will tell you if that happened.
            </div>
          </div>
        )}

        <div className="qw-grid-2">
          <LabeledInput label="Contract name">
            <Form.Control className="qw-input" value={d.contractName} disabled={saving}
              onChange={(e) => set("contractName", e.target.value)} placeholder="e.g. Mercy Health 2026" />
          </LabeledInput>
          <LabeledInput label="Contract reference" hint="How Finance and the hospital refer to it, e.g. SSC-0001.">
            <Form.Control className="qw-input" value={d.contractRef} disabled={saving}
              onChange={(e) => set("contractRef", e.target.value)} />
          </LabeledInput>
          <LabeledInput label="Signing entity">
            <Form.Control className="qw-input" value={d.signingEntityName} disabled={saving}
              onChange={(e) => set("signingEntityName", e.target.value)} placeholder="Who signed the paper" />
          </LabeledInput>
          {/* SEARCH, DON'T TYPE. This was a bare number box, which asked somebody to know an
              Epicor CustNum by heart — so the first thing anyone did was type a name into it and
              get told it had to be a whole number.
              buildCustomerConfig filters server-side on
                  startswith(CustID,'…') or contains(Name,'…')
              so the same box finds 3PROSP772 or "Live Test", and it preloads through the session
              cache the rest of the app already warms. Searching server-side is the point: a
              client-side filter over a fetched page silently hides everything past it. */}
          <LabeledInput label="Signing entity — search by ID or name"
                        hint="Optional. Who signed the contract. Leave blank for a group contract with no single signatory.">
            <SearchableSelect
              config={customerCfg}
              value={d.signingCustId}
              onChange={(v) => set("signingCustId", v)}
              onSelectItem={(it) => {
                // Keep BOTH: the number is what the server stores, the ID is what a human reads
                // back off the screen. Storing only the number would make this field unreadable
                // the moment somebody reopens the form.
                const num = pickField(it, "custNum");
                setD((x) => ({
                  ...x,
                  signingCustId: pickField(it, "custID") || "",
                  signingCustNum: num === undefined || num === null ? "" : String(num),
                  // Only fill the entity name if the user has not written their own.
                  signingEntityName: x.signingEntityName?.trim() ? x.signingEntityName : (pickField(it, "name") || ""),
                }));
              }}
              placeholder="Type a customer ID or name…"
              inputWidth="100%"
              disabled={saving}
            />
            {d.signingCustNum ? (
              <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 4 }}>
                CustNum <span className="qw-mono" style={{ color: "#4F46E5" }}>{d.signingCustNum}</span>
                {" · "}
                <button type="button" className="qw-btn qw-btn-ghost qw-btn-sm"
                  style={{ padding: "0 4px" }} disabled={saving}
                  onClick={() => setD((x) => ({ ...x, signingCustId: "", signingCustNum: "" }))}>
                  clear
                </button>
              </div>
            ) : null}
          </LabeledInput>
          <LabeledInput label="Reference price year" hint="Which year's price book this contract is pegged to.">
            <Form.Control className="qw-input" inputMode="numeric" value={d.referencePriceYear} disabled={saving}
              onChange={(e) => set("referencePriceYear", e.target.value)} />
          </LabeledInput>
          <div />
          <LabeledInput label="Starts">
            <Form.Control className="qw-input" type="date" value={d.contractStartDate} disabled={saving}
              onChange={(e) => set("contractStartDate", e.target.value)} />
          </LabeledInput>
          <LabeledInput label="Ends">
            <Form.Control className="qw-input" type="date" value={d.contractEndDate} disabled={saving}
              onChange={(e) => set("contractEndDate", e.target.value)} />
          </LabeledInput>
        </div>

        {/* The signed adjustment. The echo below is the whole point of this
            block: "10%" on its own cannot be read as cheaper or dearer, and a
            sign typed backwards overcharges a hospital on every quote until
            someone notices. */}
        <div style={{ marginTop: 18 }}>
          <label className="qw-label">Adjustment on the book price (signed)</label>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Form.Control className="qw-input" inputMode="decimal" disabled={saving}
              value={d.adjustPercent} onChange={(e) => set("adjustPercent", e.target.value)}
              placeholder="-10" style={{ maxWidth: 160, fontSize: 18, fontWeight: 700 }} />
            <span style={{ fontSize: 13, color: "#64748B" }}>
              minus = cheaper &nbsp;·&nbsp; plus = dearer
            </span>
          </div>
          <div style={{ ...ECHO_STYLE[echo.tone], borderRadius: 12, padding: "12px 14px", marginTop: 10, fontSize: 14.5, fontWeight: 700, lineHeight: 1.45 }}>
            {echo.text}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {["-10", "-5", "0", "5", "10"].map((v) => (
              <button key={v} type="button" className="qw-btn qw-btn-outline qw-btn-sm" disabled={saving}
                onClick={() => set("adjustPercent", v)}>{v === "0" ? "0" : (Number(v) > 0 ? `+${v}` : v)}</button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <label className="qw-label">Notes</label>
          <Form.Control as="textarea" rows={3} className="qw-input" value={d.notes} disabled={saving}
            onChange={(e) => set("notes", e.target.value)} />
        </div>
      </Modal.Body>
        {!editing && (
          // Documents anchor to a contract by id, and this one has none until it is saved. Rather
          // than a disabled upload box that looks broken, say where they go. The alternative —
          // staging a file against a contract that might never be created — leaves orphaned blobs
          // nobody owns.
          <div className="qw-info qw-info-indigo" style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12.5 }}>
              <strong>The signed copy goes on next.</strong> Create the contract first, then attach
              the signed PDF, amendments and the price workbook in the <strong>Documents</strong>
              {" "}section on its page — the same attachment lane quotes and orders use.
            </div>
          </div>
        )}

      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-primary" disabled={saving} onClick={save}>
          {saving ? "Saving…" : editing ? "Save contract" : "Create contract"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// ── approve modal ────────────────────────────────────────────────────────────
